import Layout from "@/shared/pages/Layout.jsx";
import ProtectedRoute from "@/shared/components/auth/ProtectedRoute";
import { auth } from "@/shared/api/graphqlClient";
import { useLanguage, LanguageProvider } from "@/shared/components/shared/LanguageContext";

// Admin Pages
import AdminDashboard from "@/shared/pages/AdminDashboard";
import Transactions from "@/shared/pages/Transactions";
import Merchants from "@/shared/pages/Merchants";
import Providers from "@/shared/pages/Providers";
import Routing from "@/shared/pages/Routing";
import WebhookLogs from "@/shared/pages/WebhookLogs";
import Deposits from "@/shared/pages/Deposits";
import Withdrawals from "@/shared/pages/Withdrawals";
import Settlements from "@/shared/pages/Settlements";
import Settings from "@/shared/pages/Settings";
import AdminResellers from "@/shared/pages/AdminResellers";
import AdminResellerForm from "@/shared/pages/AdminResellerForm";
import AdminResellerDetail from "@/shared/pages/AdminResellerDetail";
import AdminEngines from "@/shared/pages/AdminEngines";
import AdminEngineForm from "@/shared/pages/AdminEngineForm";
import AdminEngineDetail from "@/shared/pages/AdminEngineDetail";
import AdminMerchantDetail from "@/shared/pages/AdminMerchantDetail";
import AdminGlobalTransactions from "@/shared/pages/AdminGlobalTransactions";
import AdminServers from "@/shared/pages/AdminServers";
import AdminStaff from "@/shared/pages/AdminStaff";
import AdminStaffForm from "@/shared/pages/AdminStaffForm";
import AdminRoles from "@/shared/pages/AdminRoles";
import AdminRoleForm from "@/shared/pages/AdminRoleForm";
import Notifications from "@/shared/pages/Notifications";

// Login Pages
import AdminLogin from "@/shared/pages/AdminLogin";
import StaffLogin from "@/shared/pages/StaffLogin";

import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

const PAGES = {
  AdminDashboard,
  Transactions,
  Merchants,
  Providers,
  Routing,
  WebhookLogs,
  Deposits,
  Withdrawals,
  Settlements,
  Settings,
  AdminResellers,
  AdminResellerForm,
  AdminResellerDetail,
  AdminEngines,
  AdminEngineForm,
  AdminEngineDetail,
  AdminMerchantDetail,
  AdminGlobalTransactions,
  AdminServers,
  AdminStaff,
  AdminStaffForm,
  AdminRoles,
  AdminRoleForm,
  Notifications,
  AdminLogin,
  StaffLogin,
};

function _getCurrentPage(url) {
  if (url.endsWith('/')) {
    url = url.slice(0, -1);
  }
  let urlLastPart = url.split('/').pop();
  if (urlLastPart.includes('?')) {
    urlLastPart = urlLastPart.split('?')[0];
  }

  const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
  return pageName || 'AdminDashboard';
}

// Component to handle root route - redirects authenticated users to dashboard, shows login otherwise
function RootRouteContent() {
  const { t } = useLanguage();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = auth.isAuthenticated();
      setIsAuthenticated(authenticated);
      setIsChecking(false);
    };

    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // If authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/AdminDashboard" replace />;
  }

  // Otherwise, show login page
  return <AdminLogin />;
}

function RootRoute() {
  return (
    <LanguageProvider>
      <RootRouteContent />
    </LanguageProvider>
  );
}

// Redirects to /AdminDashboard if admin/staff_admin already logged in; otherwise shows AdminLogin.
function AdminLoginRouteContent() {
  const { t } = useLanguage();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(auth.isAuthenticated());
      setUserRole(auth.getUserRole()?.toUpperCase() || null);
      setIsChecking(false);
    };
    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && ['ADMIN', 'STAFF_ADMIN'].includes(userRole)) {
    return <Navigate to="/AdminDashboard" replace />;
  }

  return <AdminLogin />;
}

function AdminLoginRoute() {
  return (
    <LanguageProvider>
      <AdminLoginRouteContent />
    </LanguageProvider>
  );
}

// Redirects to /AdminDashboard if admin/staff_admin already logged in; otherwise shows StaffLogin.
function StaffLoginRouteContent() {
  const { t } = useLanguage();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(auth.isAuthenticated());
      setUserRole(auth.getUserRole()?.toUpperCase() || null);
      setIsChecking(false);
    };
    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && ['ADMIN', 'STAFF_ADMIN'].includes(userRole)) {
    return <Navigate to="/AdminDashboard" replace />;
  }

  return <StaffLogin panelType="ADMIN" />;
}

function StaffLoginRoute() {
  return (
    <LanguageProvider>
      <StaffLoginRouteContent />
    </LanguageProvider>
  );
}

// Gates protected admin routes: auth check and full-screen loading (or redirect) before Layout.
// Prevents logged-out users from seeing the Layout shell on e.g. /Transactions, /Merchants.
function ProtectedLayoutContent({ currentPageName, children }) {
  const { t } = useLanguage();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = auth.isAuthenticated();
      setIsAuthenticated(authenticated);
      setIsChecking(false);
    };
    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Layout currentPageName={currentPageName}>{children}</Layout>;
}

function ProtectedLayout({ currentPageName, children }) {
  return (
    <LanguageProvider>
      <ProtectedLayoutContent currentPageName={currentPageName}>{children}</ProtectedLayoutContent>
    </LanguageProvider>
  );
}

function AdminAppContent() {
  const location = useLocation();
  const currentPage = _getCurrentPage(location.pathname);

  // Check if current route is a login page
  const isLoginPage = ['/', '/admin.html', '/login', '/staff-login', '/AdminLogin', '/StaffLogin'].includes(location.pathname);

  // If it's a login page, render without Layout
  if (isLoginPage) {
    return (
      <Routes>
        {/* Root route - shows login or redirects to dashboard if authenticated */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/admin.html" element={<RootRoute />} />
        <Route path="/login" element={<AdminLoginRoute />} />
        <Route path="/AdminLogin" element={<AdminLoginRoute />} />
        <Route path="/staff-login" element={<StaffLoginRoute />} />
        <Route path="/StaffLogin" element={<StaffLoginRoute />} />
      </Routes>
    );
  }

  // Otherwise, render with ProtectedLayout (auth gate then Layout)
  return (
    <ProtectedLayout currentPageName={currentPage}>
      <Routes>
        {/* Admin Routes - Protected */}
        <Route path="/AdminDashboard" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/Transactions" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Transactions /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Transactions /></ProtectedRoute>} />
        <Route path="/Merchants" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Merchants /></ProtectedRoute>} />
        <Route path="/merchants" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Merchants /></ProtectedRoute>} />
        <Route path="/Providers" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Providers /></ProtectedRoute>} />
        <Route path="/providers" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Providers /></ProtectedRoute>} />
        <Route path="/Routing" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Routing /></ProtectedRoute>} />
        <Route path="/routing" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Routing /></ProtectedRoute>} />
        <Route path="/WebhookLogs" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><WebhookLogs /></ProtectedRoute>} />
        <Route path="/webhooks" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><WebhookLogs /></ProtectedRoute>} />
        <Route path="/Deposits" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Deposits /></ProtectedRoute>} />
        <Route path="/deposits" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Deposits /></ProtectedRoute>} />
        <Route path="/Withdrawals" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Withdrawals /></ProtectedRoute>} />
        <Route path="/withdrawals" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Withdrawals /></ProtectedRoute>} />
        <Route path="/Settlements" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Settlements /></ProtectedRoute>} />
        <Route path="/settlements" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Settlements /></ProtectedRoute>} />
        <Route path="/Settings" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Settings /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Settings /></ProtectedRoute>} />
        <Route path="/AdminEngines" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminEngines /></ProtectedRoute>} />
        <Route path="/engines" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminEngines /></ProtectedRoute>} />
        <Route path="/AdminEngineForm" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminEngineForm /></ProtectedRoute>} />
        <Route path="/engines/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminEngineForm /></ProtectedRoute>} />
        <Route path="/AdminEngineDetail" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminEngineDetail /></ProtectedRoute>} />
        <Route path="/AdminResellers" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminResellers /></ProtectedRoute>} />
        <Route path="/resellers" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminResellers /></ProtectedRoute>} />
        <Route path="/AdminResellerForm" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminResellerForm /></ProtectedRoute>} />
        <Route path="/resellers/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminResellerForm /></ProtectedRoute>} />
        <Route path="/AdminResellerDetail" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminResellerDetail /></ProtectedRoute>} />
        <Route path="/AdminMerchantDetail" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminMerchantDetail /></ProtectedRoute>} />
        <Route path="/AdminGlobalTransactions" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminGlobalTransactions /></ProtectedRoute>} />
        <Route path="/global-transactions" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminGlobalTransactions /></ProtectedRoute>} />
        <Route path="/AdminServers" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminServers /></ProtectedRoute>} />
        <Route path="/servers" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminServers /></ProtectedRoute>} />
        <Route path="/AdminStaff" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminStaff /></ProtectedRoute>} />
        <Route path="/staff" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminStaff /></ProtectedRoute>} />
        <Route path="/AdminStaffForm" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminStaffForm /></ProtectedRoute>} />
        <Route path="/staff/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminStaffForm /></ProtectedRoute>} />
        <Route path="/AdminRoles" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminRoles /></ProtectedRoute>} />
        <Route path="/roles" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminRoles /></ProtectedRoute>} />
        <Route path="/AdminRoleForm" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminRoleForm /></ProtectedRoute>} />
        <Route path="/roles/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><AdminRoleForm /></ProtectedRoute>} />
        <Route path="/Notifications" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Notifications /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute allowedRoles={['ADMIN', 'STAFF_ADMIN']}><Notifications /></ProtectedRoute>} />

        {/* Catch-all redirect to root (login) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ProtectedLayout>
  );
}

export default function AdminApp() {
  return (
    <Router>
      <AdminAppContent />
    </Router>
  );
}

